/** @jsx jsx */
import { ReactNode } from 'react';
import { css, jsx } from '@emotion/react';
import { Link } from 'gatsby';

import { CustomItem, CustomItemProps } from '@atlaskit/menu';
import { B100, N0, N800, N20 } from '@atlaskit/theme/colors';
import { gridSize } from '@atlaskit/theme/constants';
import { token } from '@atlaskit/tokens';
import { HeadingIcon } from '@af/design-system-docs-ui';

interface HeadingItemProps {
  children: ReactNode;
  indexName: string;
}

const headingItemStyles = css({
  display: 'flex',
  flexDirection: 'row',
  padding: `${token('space.150', '12px')} ${token('space.300', '24px')}`,
  whiteSpace: 'normal',
  backgroundColor: token('color.background.neutral', N0),
  fontSize: token('font.size.075', '12px'),
  color: token('color.text', N800),
  lineHeight: token('font.lineHeight.100', '16px'),
  textTransform: 'uppercase',
  // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage-spacing
  fontFamily: 'Charlie Display',
});

export const HeadingItem = ({ children, indexName }: HeadingItemProps) => {
  return (
    <CustomItem
      component={({ children }) => (
        <div css={headingItemStyles}>
          <HeadingIcon heading={indexName} />
          {children}
        </div>
      )}
    >
      {children}
    </CustomItem>
  );
};

interface LinkItemProps extends CustomItemProps {
  to: string;
  children: ReactNode;
}

const linkItemStyles = css({
  // FIXME: !important use
  padding: `${token('space.150', '12px')} ${token(
    'space.300',
    '24px',
  )} !important`,
  color: 'inherit',
  outline: 'none',
  tabIndex: 0,
  textDecoration: 'none',
  '& p': {
    lineHeight: token('font.lineHeight.300', '24px'),
  },
  '&:hover': {
    color: 'inherit',
    textDecoration: 'none',
    backgroundColor: token('color.background.neutral.subtle.hovered', N20),
    outline: 'none',
  },
  '&:focus': {
    color: 'inherit',
    boxShadow: `0px 0px 0px ${gridSize() / 4}px inset ${token(
      'color.border.focused',
      B100,
    )};`,
    textDecoration: 'none',
    outline: 'none',
  },
  '&: active': {
    textDecoration: 'none',
    outline: 'none',
    backgroundColor: token('color.background.neutral.subtle.pressed', N20),
  },
  em: {
    fontWeight: token('font.weight.bold', '700'),
    fontStyle: 'inherit',
  },
});

export const LinkItem = ({ to, children, ...props }: LinkItemProps) => (
  <CustomItem
    {...props}
    // @ts-expect-error wrapperClass not a prop?
    component={({ wrapperClass, ...rest }) => (
      <Link css={linkItemStyles} to={to} {...rest} />
    )}
  >
    {children}
  </CustomItem>
);
